import { Heading, Stack, Text } from "@biblioteksentralen/react";
import { DateHelper, ResolvedEventSummary } from "@libry-content/common";
import isAfter from "date-fns/isAfter";
import isBefore from "date-fns/isBefore";
import { Suspense } from "react";
import { EventListData } from "../../app/[locale]/sites/[siteDomain]/arrangementer/page";
import { useTranslation } from "../../utils/hooks/useTranslation";
import { useSitePalette } from "../../utils/useSitePalette";
import { DefaultContainer } from "../ContentContainer";
import { CreateNew } from "../editInSanity/EditInSanity";
import { FeaturedEventBanner } from "../site/banners/events/FeaturedEventBanner";
import { EventsList } from "./EventsList";
import { EventsWithFilters } from "./EventsWithFilters";

export const UpcomingEvents = (props: Pick<EventListData, "initialData" | "hasUnfinishedEvents">) => {
  const { t } = useTranslation();
  const palette = useSitePalette();

  // Vi ønsker ikke å vise kansellerte eller ferdige events i topp-banneret.
  // Vi antar her at "allEvents" allerede er filtrert slik at den bare inneholder events for i dag eller senere.
  const unfinishedUncancelledEvents = props.initialData.events.filter((event) => {
    if (event.eventStatus === "cancelled") return false;
    if (!event.eventSchedule?.endsAt) return false; // Denne staten skal ikke være mulig pga validering i studio, men må håndtere siden typene sier at de kan være undefined
    const endsAt = new DateHelper(event.eventSchedule.endsAt);
    return endsAt.isAfterNow(); // Sjekker om arrangementet er over
  });

  const featuredEvent =
    unfinishedUncancelledEvents.find(isCurrentlyInPromotionPeriod) || // Prioriterer å vise arrangementer som er merket som fremhevet
    unfinishedUncancelledEvents[0]; // Fallbacker til neste arrangement dersom ingen arrangementer er merket som fremhevet

  return (
    <Stack spacing="2rem">
      <DefaultContainer>
        <Heading as="h1">
          {props.hasUnfinishedEvents ? t("Dette skjer hos oss") : t("Ingen planlagte arrangementer")}
        </Heading>
      </DefaultContainer>
      {featuredEvent && (
        <DefaultContainer withoutPaddingXBelow="md">
          <FeaturedEventBanner event={featuredEvent} bannerColor={palette.colors.lightaccent2} />
        </DefaultContainer>
      )}
      <DefaultContainer>
        <CreateNew type="event" />
      </DefaultContainer>
      {props.hasUnfinishedEvents ? (
        <DefaultContainer>
          <Suspense fallback={<EventsList events={props.initialData.events} />}>
            <EventsWithFilters initialData={props.initialData} finishedOrUnfinishedEvents="unfinished" />
          </Suspense>
        </DefaultContainer>
      ) : (
        <DefaultContainer>
          <Text>{t("Men kom gjerne tilbake senere.")}</Text>
        </DefaultContainer>
      )}
    </Stack>
  );
};

const isCurrentlyInPromotionPeriod = (event: ResolvedEventSummary) => {
  if (!event.promotion) return false;
  const { startsAt, endsAt } = event.promotion;
  if (!endsAt || !startsAt) return false;
  if (isBefore(new Date(), new Date(startsAt))) return false;
  if (isAfter(new Date(), new Date(endsAt))) return false;
  return true;
};
